/**
 * Per-day-cell workload indicator display logic.
 *
 * Single source of truth for how one user's workload on one day (or one month,
 * in year view) is turned into a rendered cell indicator: the fill bar width,
 * colors, and the value label. Used by the scheduler's per-cell DOM overlay.
 *
 * This is presentation only — it consumes the already-computed `WorkloadEntry`
 * fields from `computeSchedulerWorkload` and never recomputes workload itself.
 */

import type { WorkloadDisplayMode, WorkloadIndicatorType } from '../stores/workloadStore';
import { getWorkloadColor, WORKLOAD_COLORS } from './colors';

/** Gray used for muted/empty labels (dashes, zero). */
const MUTED_LABEL_COLOR = '#97A0AF';

/**
 * The subset of `WorkloadEntry` / `AggregatedEntry` fields the cell indicator
 * needs. Both the per-day `WorkloadEntry` and the per-month `AggregatedEntry`
 * satisfy this shape.
 */
export interface WorkloadCellInput {
  scheduledHours: number;
  capacityHours: number;
  availableHours: number;
  isHoliday: boolean;
  isLeave: boolean;
  leaveDayType: 'full' | 'am' | 'pm' | null;
  hasLeaveConflict: boolean;
  worklogHours?: number;
  isPastDay?: boolean;
}

/** Everything the DOM injection layer needs to render one cell's indicator. */
export interface WorkloadCellDisplay {
  /** When false, no indicator should be drawn for this cell. */
  render: boolean;
  /** Draw the track + fill bar (false = label only, e.g. a dash or item count). */
  showTrack: boolean;
  /** Fill bar color. */
  fillColor: string;
  /** Fill width, 0–100 (%). */
  fillPct: number;
  /** Diagonal-stripe the fill (leave / holiday / conflict). */
  striped: boolean;
  /** Optional fill opacity (dimmed leave/holiday bars). */
  fillOpacity?: number;
  /** Overflow (>100%) segment width, 0–100 (%); 0 = none. */
  overflowPct: number;
  /** Overflow segment color. */
  overflowColor: string;
  /** Value label text (e.g. "6/8h", "75%", "–", "3", "⚠"). */
  label: string;
  /** Value label color. */
  labelColor: string;
  /** Conflict state (items scheduled on a full leave/holiday with no room). */
  conflict: boolean;
  /** Tooltip text for conflict/holiday, if any. */
  title?: string;
}

const round2 = (v: number): number => Math.round(v * 100) / 100;

const EMPTY: WorkloadCellDisplay = {
  render: false,
  showTrack: false,
  fillColor: WORKLOAD_COLORS.empty,
  fillPct: 0,
  striped: false,
  overflowPct: 0,
  overflowColor: WORKLOAD_COLORS.empty,
  label: '',
  labelColor: MUTED_LABEL_COLOR,
  conflict: false,
};

/**
 * Compute the cell indicator for a single (user, day/month) workload entry.
 *
 * Mirrors the mode semantics documented in `specs/workload-calculation.md`
 * §12 and the per-cell rendering of the WorkHub Jira scheduler, but reuses this
 * app's richer `getWorkloadColor` for all coloring decisions.
 */
export function getWorkloadCellDisplay(
  entry: WorkloadCellInput,
  issueCount: number,
  indicatorType: WorkloadIndicatorType | null,
  displayMode: WorkloadDisplayMode,
): WorkloadCellDisplay {
  const {
    scheduledHours,
    capacityHours,
    availableHours,
    isHoliday,
    isLeave,
    leaveDayType,
    hasLeaveConflict,
    worklogHours,
    isPastDay,
  } = entry;

  const isNonWorking = capacityHours <= 0 && !isHoliday && !isLeave;

  // Number-of-items mode: just the count, no bar. Nothing on empty days.
  if (indicatorType === 'issues') {
    if (issueCount <= 0) return EMPTY;
    return {
      ...EMPTY,
      render: true,
      label: String(issueCount),
      labelColor: '#676879',
    };
  }

  // Conflict: items landed on a full leave / holiday day (redistribution failed).
  const conflict =
    (isHoliday && (scheduledHours > 0 || hasLeaveConflict)) ||
    (isLeave && leaveDayType === 'full' && (scheduledHours > 0 || hasLeaveConflict));

  if (conflict) {
    // Holiday takes color precedence over the leave-conflict purple (spec §5, and
    // the capacity popup colors the same day orange). Leave conflicts stay purple.
    const conflictColor = isHoliday ? WORKLOAD_COLORS.holiday : WORKLOAD_COLORS.conflict;
    return {
      ...EMPTY,
      render: true,
      showTrack: true,
      fillColor: conflictColor,
      fillPct: 100,
      striped: true,
      label: '⚠',
      labelColor: conflictColor,
      conflict: true,
      title: isHoliday
        ? scheduledHours > 0
          ? 'Holiday conflict: items scheduled on holiday'
          : 'Holiday'
        : 'Leave conflict: items scheduled on leave day',
    };
  }

  // Plain non-working day (weekend) with nothing on it → no indicator.
  if (isNonWorking) return EMPTY;

  // Remaining-estimate / availability modes: past days collapse to a dash — except
  // holidays and full-day leaves, which keep their striped bar so the day still
  // reads as "off" (matches the color logic for non-past holidays/leaves).
  const showDash =
    !!isPastDay &&
    (indicatorType === 'remaining_estimate' || indicatorType === 'availability') &&
    !isHoliday &&
    !(isLeave && leaveDayType === 'full');

  if (showDash) {
    // A dash on a leave day (e.g. half-day) keeps its vacation-blue tint.
    const dashColor = getWorkloadColor(
      scheduledHours, availableHours, isLeave, leaveDayType, hasLeaveConflict,
      isHoliday, isPastDay, indicatorType ?? undefined, worklogHours,
    );
    return {
      ...EMPTY,
      render: true,
      label: '–',
      labelColor: dashColor === WORKLOAD_COLORS.empty ? MUTED_LABEL_COLOR : dashColor,
    };
  }

  const isFullHoliday = isHoliday;
  const isFullLeave = isLeave && leaveDayType === 'full' && availableHours <= 0;

  if (isFullHoliday || isFullLeave) {
    const color = isFullHoliday ? WORKLOAD_COLORS.holiday : WORKLOAD_COLORS.vacation;
    return {
      ...EMPTY,
      render: true,
      showTrack: true,
      fillColor: color,
      fillPct: 100,
      striped: true,
      fillOpacity: 0.4,
      label: '–',
      labelColor: color,
      title: isFullHoliday ? 'Holiday' : undefined,
    };
  }

  const color = getWorkloadColor(
    scheduledHours,
    availableHours,
    isLeave,
    leaveDayType,
    hasLeaveConflict,
    isHoliday,
    isPastDay,
    indicatorType ?? undefined,
    worklogHours,
  );

  // Availability mode: bar/label represent FREE hours (capacity left).
  if (indicatorType === 'availability') {
    const free = Math.max(0, availableHours - scheduledHours);
    const overbooked = scheduledHours > availableHours && availableHours > 0;
    const fillPct = availableHours > 0 ? Math.min((free / availableHours) * 100, 100) : 0;
    const overflowPct = overbooked
      ? Math.min(((scheduledHours - availableHours) / availableHours) * 100, 100)
      : 0;

    let label: string;
    if (displayMode === 'hours') {
      label = overbooked ? `-${round2(scheduledHours - availableHours)}h` : `${round2(free)}h`;
    } else {
      const pct = availableHours > 0 ? Math.round(((availableHours - scheduledHours) / availableHours) * 100) : 0;
      label = `${pct}%`;
    }

    return {
      ...EMPTY,
      render: true,
      showTrack: true,
      fillColor: color,
      fillPct: overbooked ? 0 : fillPct,
      overflowPct,
      overflowColor: WORKLOAD_COLORS.red,
      label,
      labelColor: overbooked ? WORKLOAD_COLORS.red : color === WORKLOAD_COLORS.empty ? MUTED_LABEL_COLOR : color,
    };
  }

  // Workload / remaining-estimate / worklog-workload: bar = scheduled / available.
  const overCapacity = scheduledHours > availableHours && availableHours > 0;
  const fillPct = availableHours > 0 ? Math.min((scheduledHours / availableHours) * 100, 100) : 0;
  const overflowPct = overCapacity
    ? Math.min(((scheduledHours - availableHours) / availableHours) * 100, 100)
    : 0;

  const label =
    displayMode === 'hours'
      ? `${round2(scheduledHours)}/${round2(availableHours)}h`
      : availableHours > 0
        ? `${Math.round((scheduledHours / availableHours) * 100)}%`
        : '0%';

  return {
    ...EMPTY,
    render: true,
    showTrack: true,
    fillColor: color,
    fillPct: overCapacity ? 100 : fillPct,
    overflowPct,
    overflowColor: color,
    label,
    labelColor: color === WORKLOAD_COLORS.empty ? MUTED_LABEL_COLOR : color,
  };
}
